import { OperationVariables, QueryHookOptions, QueryResult } from '@apollo/client'
import deepmerge from 'deepmerge'
import { Maybe } from 'gql'
import React, { useCallback, useState } from 'react'
import { Button, ButtonProps, Loader } from 'semantic-ui-react'
type Result<E> = { pageInfo: { hasNextPage?: boolean; endCursor?: Maybe<string> }; edges?: E }

type QueryHookOptionsWithRequiredVariables<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = Omit<QueryHookOptions<TData, TVariables>, 'variables'> & {
  variables: Exclude<QueryHookOptions<TData, TVariables>['variables'], undefined>
}

/**
 * Hook for setting up a graphql query with pagination.
 *
 * returns { edges } (null if still loading),
 * { pagination } for use with the helper components below,
 * and the standard apollo response fields such as { loading }.
 */
export const usePaginatedQuery = <T, V extends OperationVariables, E>(
  useQuery: (options: QueryHookOptionsWithRequiredVariables<T, V>) => QueryResult<T, V>,
  queryOpts: QueryHookOptionsWithRequiredVariables<T, V>,
  accessor: (e: T) => Result<E> | null | undefined
) => {
  const [loadingMore, setLoadingMore] = useState(false)
  const response = useQuery(queryOpts)

  const { variables: rv, fetchMore, data } = response
  const collection = (data && accessor(data)) || undefined
  const edges = ((collection && collection?.edges) as []) || undefined
  const hasNext = collection && collection?.pageInfo.hasNextPage

  const loadMore = useCallback(() => {
    setLoadingMore(true)

    if (!collection?.pageInfo.endCursor) return Promise.resolve()
    return fetchMore({
      variables: Object.assign({}, rv, { cursor: collection.pageInfo.endCursor }),
      updateQuery: (prevResult, { fetchMoreResult }) =>
        (fetchMoreResult && deepmerge(prevResult, fetchMoreResult)) || prevResult,
    }).then(() => setLoadingMore(false))
  }, [collection, fetchMore, rv])

  return { edges, pagination: { hasNext, loadingMore, loadMore }, ...response }
}

type PaginationProps = { pagination: { hasNext?: boolean; loadingMore: boolean; loadMore: () => Promise<void> } }

// Convenience button helper for pagination reload. Simply pass
// the usePaginatedQuery's result.pagination as `pagination`, all other props
// will be used on the button itself
export const PaginationButton = ({
  pagination: { hasNext, loadingMore, loadMore },
  ...buttonProps
}: PaginationProps & ButtonProps) => {
  if (!hasNext) return null

  return <Button size="large" loading={loadingMore} onClick={loadMore} {...buttonProps} />
}

export const PaginationLink = ({
  children,
  content,
  pagination: { hasNext, loadingMore, loadMore },
}: PaginationProps & { children?: React.ReactNode; content?: React.ReactNode }) => {
  if (loadingMore) return <Loader active inline="centered" />
  if (!hasNext) return null

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault()
        loadMore()
      }}
    >
      {content || children}
    </a>
  )
}
